// src/modules/COGS/COGSTab.js
import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiDollarSign } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const COGSTab = () => {
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [isAddingItem, setIsAddingItem] = useState(false);

  useEffect(() => {
    // Mock data - replace with actual API calls
    setItems([
      { 
        id: 1, 
        name: 'DROVER BEEF BURGER', 
        cost: 7.94, 
        price: 21.74, 
        margin: 63.48,
        ingredients: [
          { name: 'Miratorg Brisket', quantity: 0.120, unit: 'Kg', unitCost: 33.00 },
          { name: 'Miratorg Chuck', quantity: 0.030, unit: 'Kg', unitCost: 33.00 },
          { name: 'Burger Bun', quantity: 1, unit: 'Pc', unitCost: 0.25 },
          { name: 'Drover Sauce', quantity: 0.030, unit: 'Kg', unitCost: 10.70 },
          { name: 'Cheese Slice', quantity: 2, unit: 'pc', unitCost: 0.56 },
          { name: 'Fresh Onion', quantity: 0.010, unit: 'Kg', unitCost: 3.50 },
          { name: 'Foil Wrapper', quantity: 1, unit: 'Pc', unitCost: 0.08 },
          { name: 'Drover Burger Box', quantity: 1, unit: 'Pc', unitCost: 0.90 },
          { name: 'Drover Plastic Bag', quantity: 1, unit: 'Pc', unitCost: 0.23 },
          { name: 'Drover Stickers', quantity: 1, unit: 'Pc', unitCost: 0.03 },
          { name: 'Plain Napkin', quantity: 1, unit: 'Pc', unitCost: 0.02 },
        ]
      },
      { 
        id: 2, 
        name: 'DROVER CRISPY CHICKEN', 
        cost: 4.77, 
        price: 16.52, 
        margin: 71.16,
        ingredients: [
          { name: 'Crispy Chicken', quantity: 1, unit: 'Pc', unitCost: 1.95 },
          { name: 'Crispy Water Mix', quantity: 0.010, unit: 'Kg', unitCost: 2.52 },
          { name: 'Flour Mix', quantity: 0.055, unit: 'Kg', unitCost: 6.30 },
          { name: 'Burger Bun', quantity: 1, unit: 'Pc', unitCost: 0.25 },
          { name: 'Coleslaw', quantity: 0.040, unit: 'Kg', unitCost: 5.81 },
          { name: 'Iceburg', quantity: 0.015, unit: 'Kg', unitCost: 8.00 },
          { name: 'Barn Sauce', quantity: 0.040, unit: 'Kg', unitCost: 12.00 },
          { name: 'Soplem Butter', quantity: 0.010, unit: 'Kg', unitCost: 10.24 },
          { name: 'Foil Wrapper', quantity: 1, unit: 'Pc', unitCost: 0.08 },
          { name: 'Drover Burger Box', quantity: 1, unit: 'Pc', unitCost: 0.90 },
          { name: 'Drover Plastic Bag', quantity: 1, unit: 'Pc', unitCost: 0.23 },
          { name: 'Drover Stickers', quantity: 1, unit: 'Pc', unitCost: 0.03 },
          { name: 'Plain Napkin', quantity: 1, unit: 'Pc', unitCost: 0.02 },
        ]
      },
    ]);
  }, []);

  const filteredItems = items.filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddItem = (newItem) => {
    setItems([...items, { ...newItem, id: items.length + 1 }]);
    setIsAddingItem(false);
  };

  const handleUpdateItem = (updatedItem) => {
    setItems(items.map(item => item.id === updatedItem.id ? updatedItem : item));
    setSelectedItem(null);
  };

  const handleDeleteItem = (id) => {
    setItems(items.filter(item => item.id !== id));
  };

  const ItemCard = ({ item, onEdit, onDelete }) => {
    const formatNumber = (value) => {
      const number = parseFloat(value);
      return isNaN(number) ? '0.00' : number.toFixed(2);
    };

    const calculateCOGS = () => {
      const cost = parseFloat(item.cost);
      const price = parseFloat(item.price);
      if (isNaN(cost) || isNaN(price) || price === 0) return '0.00';
      return ((cost / price) * 100).toFixed(2);
    };

    return (
      <motion.div 
        layout
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
      >
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-semibold">{item.name}</h3>
          <div className="flex space-x-2">
            <button onClick={() => onEdit(item)} className="text-blue-500 hover:text-blue-700">
              <FiEdit2 />
            </button>
            <button onClick={() => onDelete(item.id)} className="text-red-500 hover:text-red-700">
              <FiTrash2 />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>Cost: ${formatNumber(item.cost)}</div>
          <div>Price: ${formatNumber(item.price)}</div>
          <div>COGS: {calculateCOGS()}%</div>
          <div>Margin: {formatNumber(item.margin)}%</div>
        </div>
      </motion.div>
    );
  };
  const ItemForm = ({ item, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState(item || { 
      name: '', 
      cost: 0, 
      price: 0, 
      margin: 0,
      ingredients: []
    });

    const handleChange = (e) => {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleIngredientChange = (index, field, value) => {
      const updatedIngredients = formData.ingredients.map((ing, i) => 
        i === index ? { ...ing, [field]: value } : ing
      );
      setFormData({ ...formData, ingredients: updatedIngredients });
    };

    const handleAddIngredient = () => {
      setFormData({
        ...formData,
        ingredients: [...formData.ingredients, { name: '', quantity: 0, unit: '', unitCost: 0 }]
      });
    };

    const handleRemoveIngredient = (index) => {
      setFormData({
        ...formData,
        ingredients: formData.ingredients.filter((_, i) => i !== index)
      });
    };

    const calculateCosts = () => {
      const totalCost = formData.ingredients.reduce((sum, ing) => 
        sum + (ing.quantity * ing.unitCost), 0
      );
      const margin = formData.price > 0 
        ? ((formData.price - totalCost) / formData.price) * 100 
        : 0;
      
      setFormData({
        ...formData,
        cost: totalCost,
        margin: margin
      });
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      calculateCosts();
      onSubmit(formData);
    };

    return (
      <motion.form 
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="bg-white p-6 rounded-lg shadow-lg"
      >
        <h2 className="text-xl font-semibold mb-4">{item ? 'Edit' : 'Add'} Item</h2>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Item Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Selling Price</label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              required
            />
          </div>
        </div>
        <h3 className="font-semibold mb-2">Ingredients</h3>
        <div className="mb-2 grid grid-cols-5 gap-2 font-medium text-sm text-gray-700">
          <div className="col-span-2">Ingredient</div>
          <div>Quantity</div>
          <div>Unit</div>
          <div>Unit Cost</div>
        </div>
        {formData.ingredients.map((ing, index) => (
          <div key={index} className="grid grid-cols-5 gap-2 mb-2">
            <input
              type="text"
              value={ing.name}
              onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
              placeholder="Ingredient"
              className="col-span-2 p-2 border rounded"
            />
            <input
              type="number"
              value={ing.quantity}
              onChange={(e) => handleIngredientChange(index, 'quantity', parseFloat(e.target.value))}
              placeholder="Quantity"
              className="p-2 border rounded"
            />
            <input
              type="text"
              value={ing.unit}
              onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
              placeholder="Unit"
              className="p-2 border rounded"
            />
            <div className="flex">
              <input
                type="number"
                value={ing.unitCost}
                onChange={(e) => handleIngredientChange(index, 'unitCost', parseFloat(e.target.value))}
                placeholder="Unit Cost"
                className="p-2 border rounded flex-grow"
              />
              <button 
                type="button" 
                onClick={() => handleRemoveIngredient(index)}
                className="ml-2 text-red-500 hover:text-red-700"
              >
                <FiX />
              </button>
            </div>
          </div>
        ))}
        <button 
          type="button" 
          onClick={handleAddIngredient}
          className="mb-4 text-blue-500 hover:text-blue-700"
        >
          <FiPlus className="inline mr-1" /> Add Ingredient
        </button>
        <div className="flex justify-end space-x-2 mt-4">
          <button 
            type="button" 
            onClick={calculateCosts}
            className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 flex items-center"
          >
            <FiDollarSign className="mr-2" /> Calculate
          </button>
          <button 
            type="button" 
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
          <button 
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            {item ? 'Update' : 'Add'} Item
          </button>
        </div>
      </motion.form>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">COGS Items</h2>
        <button 
          onClick={() => setIsAddingItem(true)}
          className="bg-green-500 text-white px-4 py-2 rounded-full hover:bg-green-600 transition-colors duration-300 flex items-center"
        >
          <FiPlus className="mr-2" /> Add New Item
        </button>
      </div>

      <div className="relative">
        <input
          type="text"
          placeholder="Search items..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-2 pl-10 pr-4 border rounded-full"
        />
        <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
      </div>

      <AnimatePresence>
        {(isAddingItem || selectedItem) && (
          <ItemForm 
            item={selectedItem} 
            onSubmit={selectedItem ? handleUpdateItem : handleAddItem} 
            onCancel={() => {
              setSelectedItem(null);
              setIsAddingItem(false);
            }}
          />
        )}
      </AnimatePresence>

      <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <AnimatePresence>
          {filteredItems.map(item => (
            <ItemCard 
              key={item.id} 
              item={item} 
              onEdit={setSelectedItem}
              onDelete={handleDeleteItem}
            />
          ))}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default COGSTab;